import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, Download, Star, Facebook as FacebookIcon, Instagram, Video, Globe } from 'lucide-react';
import axios from 'axios';

const AuthorEcosystemSalesPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Book cover images
  const covers = {
    facebook: "https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/rnk6gz3y_THE%20Ultimate%20Authors%20FACEbook%20cover.png",
    instagram: "https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png",
    tiktok: "https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/g4dpspzt_Ultimate%20Author%20TIKTOK%20eCoSystem%20front%20cover.png",
    resourceGuide: "https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/l3735rry_AUTHOR%20RESOURCE%20GUIDE%20-%20FINAL.png",
    bonusCase: "https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/46y75gw2_FREE%20-%20HOW%20THE%20ULTIMATE%20AUTHOR%20eCoSystem%20creates%20BestSellers.png"
  };

  const handlePayment = async (packageId) => {
    setIsProcessing(true);
    setSelectedPackage(packageId);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API_URL}/api/create-checkout-session`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Unable to process payment. Please try again or contact support.');
      setIsProcessing(false);
      setSelectedPackage(null);
    }
  };

  const scrollToPricing = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 via-green-500 to-green-700 text-white py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/67pzcjdk_eBlackBookListings.png" 
              alt="eBlackBookListings"
              className="h-16 md:h-20 mx-auto"
            />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            Stop Wasting Hours Searching for<br/>Where to Promote Your Book
          </h1>
          
          <p className="text-2xl md:text-3xl font-bold text-yellow-300 mb-4">
            Get 400+ Verified Platforms Where MILLIONS of Readers Are Actively Looking for Their Next Great Book
          </p>
          
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
            The Complete Author Social Media eCoSystem: From Unknown Author to Bestseller in 90 Days
          </p>
          
          <Button 
            onClick={scrollToPricing}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white text-xl md:text-2xl font-black px-12 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200"
          >
            GET INSTANT ACCESS NOW →
          </Button>
          
          <div className="mt-6 text-lg md:text-xl">
            <span className="line-through opacity-80">Regular Price: $174</span>
            <span className="ml-4 text-yellow-300 font-bold text-2xl">Complete Collection: Just $97</span>
          </div>
          <p className="mt-2 text-base opacity-90">Less than 25¢ per platform. Instant digital download.</p>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-5xl font-black text-red-600 text-center mb-6">
            See Exactly What You're Getting
          </h2>
          <p className="text-lg text-center text-gray-700 mb-12 max-w-4xl mx-auto">
            Four beautifully designed, professionally curated resources PLUS the premium Author Resource Guide. 
            Each PDF is landscape format with live clickable links.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Facebook eCoSystem */}
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-blue-600 text-white text-center py-2 px-4">
                <Badge className="bg-white text-blue-600 font-bold">SOCIAL MEDIA</Badge>
              </div>
              <div className="p-6">
                <img 
                  src={covers.facebook} 
                  alt="Facebook eCoSystem" 
                  className="w-full rounded-lg shadow-lg mb-4"
                />
                <h3 className="text-xl font-bold text-green-600 mb-2">Facebook eCoSystem</h3>
                <p className="text-gray-700 text-sm">
                  <strong>100+ verified reader & author groups</strong> where you can connect with engaged book lovers.
                </p>
              </div>
            </Card>

            {/* TikTok eCoSystem */}
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-black text-white text-center py-2 px-4">
                <Badge className="bg-white text-black font-bold">SOCIAL MEDIA</Badge>
              </div>
              <div className="p-6">
                <img 
                  src={covers.tiktok} 
                  alt="TikTok eCoSystem" 
                  className="w-full rounded-lg shadow-lg mb-4"
                />
                <h3 className="text-xl font-bold text-green-600 mb-2">TikTok eCoSystem</h3>
                <p className="text-gray-700 text-sm">
                  <strong>100+ BookTok creators</strong> with combined 50M+ followers making books go viral daily.
                </p>
              </div>
            </Card>

            {/* Instagram eCoSystem */}
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 px-4">
                <Badge className="bg-white text-purple-600 font-bold">SOCIAL MEDIA</Badge>
              </div>
              <div className="p-6">
                <img 
                  src={covers.instagram} 
                  alt="Instagram eCoSystem" 
                  className="w-full rounded-lg shadow-lg mb-4"
                />
                <h3 className="text-xl font-bold text-green-600 mb-2">Instagram eCoSystem</h3>
                <p className="text-gray-700 text-sm">
                  <strong>100+ Bookstagram accounts</strong> including major influencers and active reviewers.
                </p>
              </div>
            </Card>

            {/* Author Resource Guide */}
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow duration-300 border-4 border-yellow-400">
              <div className="bg-red-600 text-white text-center py-2 px-4">
                <Badge className="bg-yellow-400 text-red-600 font-bold">PREMIUM GUIDE</Badge>
              </div>
              <div className="p-6">
                <img 
                  src={covers.resourceGuide} 
                  alt="Author Resource Guide" 
                  className="w-full rounded-lg shadow-lg mb-4"
                />
                <h3 className="text-xl font-bold text-green-600 mb-2">Author Resource Guide</h3>
                <p className="text-gray-700 text-sm">
                  <strong>The complete step-by-step blueprint</strong> to publish, sell, and profit from your books.
                </p>
              </div>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              onClick={scrollToPricing}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white text-xl font-black px-12 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200"
            >
              GET ALL 4 RESOURCES NOW →
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-5xl font-black text-red-600 text-center mb-8">
            Are You Making These Deadly Author Marketing Mistakes?
          </h2>
          
          <p className="text-xl text-center text-gray-700 mb-12 max-w-4xl mx-auto">
            You wrote an amazing book. But now it's sitting on Amazon with 2 sales (both to family members) 
            while other authors are hitting bestseller lists...
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Wasting Hours on Google", desc: "Spending 20+ hours searching 'how to promote my book' and getting overwhelmed" },
              { title: "Random Promotion", desc: "Posting everywhere randomly, hoping something sticks, with zero strategy" },
              { title: "Invisible on Social Media", desc: "Your posts get 3 likes (all from your mom) while others build massive followings" },
              { title: "No Reviews or Sales", desc: "Your book has been out for months but you can't get reviewers to notice you" },
              { title: "Afraid to 'Spam'", desc: "You know you need to market but don't want to be annoying so you do... nothing" },
              { title: "No Time or Money", desc: "Marketing agencies want $5K+/month. You have $0 budget and 2 hours/day" }
            ].map((problem, idx) => (
              <Card key={idx} className="p-6 border-l-4 border-red-600 hover:shadow-xl transition-shadow duration-200">
                <h3 className="text-xl font-bold text-red-600 mb-3">❌ {problem.title}</h3>
                <p className="text-gray-700">{problem.desc}</p>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <h3 className="text-3xl font-bold text-green-600 mb-4">Sound Familiar?</h3>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              You're not alone. <strong>97% of self-published authors sell fewer than 100 books.</strong> Not because their books aren't good. 
              But because they don't know WHERE their readers are or HOW to reach them effectively.
            </p>
            <p className="text-2xl font-black text-red-600 mt-6">That changes TODAY.</p>
          </div>
        </div>
      </section>

      {/* Solution with Product Images */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-br from-green-600 via-green-500 to-green-700 text-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-6 text-yellow-300">
            Introducing: The Ultimate Author eCoSystem
          </h2>
          
          <p className="text-xl md:text-2xl text-center mb-12 max-w-4xl mx-auto">
            400+ Verified Platforms Where Your Readers Are ACTIVELY Looking for Books Like Yours
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { img: covers.resourceGuide, title: "Website Resource", count: "100+", desc: "BookBub, Goodreads, Amazon Author Central, genre-specific sites" },
              { img: covers.facebook, title: "Facebook Resource", count: "100+", desc: "10M+ combined members in active reader & author groups" },
              { img: covers.instagram, title: "Instagram Resource", count: "100+", desc: "Major influencers, reviewers seeking ARCs, Black bookstagram community" },
              { img: covers.tiktok, title: "TikTok Resource", count: "100+", desc: "50M+ combined followers, creators making books go viral" }
            ].map((resource, idx) => (
              <Card key={idx} className="bg-white text-gray-900 p-6 text-center hover:scale-105 transition-transform duration-200 shadow-xl">
                <img 
                  src={resource.img} 
                  alt={resource.title} 
                  className="w-full max-w-[180px] mx-auto rounded-lg shadow-lg mb-4"
                />
                <h3 className="text-lg font-bold text-green-600 mb-2">{resource.title}</h3>
                <div className="text-4xl font-black text-red-600 my-3">{resource.count}</div>
                <p className="text-sm text-gray-700">{resource.desc}</p>
              </Card>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-black text-center mb-6 text-yellow-300">Every Platform Includes:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[
                "Live Clickable Links",
                "Follower/Member Counts",
                "Cost Breakdown (Free/Paid)",
                "Platform Focus & Notes",
                "Best Practices Guide",
                "Engagement Strategies"
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-yellow-300 flex-shrink-0" />
                  <span className="text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center">
            <Button 
              onClick={scrollToPricing}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white text-xl font-black px-12 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200"
            >
              YES! GIVE ME ACCESS NOW →
            </Button>
          </div>
        </div>
      </section>

      {/* FREE Bonus Section */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl md:text-5xl font-black text-red-600 mb-6">
            🎁 FREE BONUS: Case Study Blueprint
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            When you get the complete eCoSystem, you also receive this exclusive guide showing exactly how 
            Jasmine went from 2 sales to bestseller in 90 days.
          </p>
          
          <img 
            src={covers.bonusCase} 
            alt="How the Ultimate Author eCoSystem Creates Bestsellers" 
            className="max-w-2xl w-full mx-auto rounded-xl shadow-2xl mb-8"
          />
          
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            <strong>FREE with bundle purchase:</strong> Step-by-step breakdown, 30-day content calendar, 
            viral formulas, DM templates, and strategy guides for all platforms.
          </p>
          
          <Button 
            onClick={scrollToPricing}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white text-xl font-black px-12 py-6 rounded-full shadow-2xl"
          >
            GET THE COMPLETE BUNDLE NOW →
          </Button>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing-section" className="py-16 md:py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-5xl font-black text-red-600 text-center mb-12">
            Choose Your Author Ecosystem Package
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {/* Individual */}
            <Card className="p-8 border-2 border-gray-300">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-600 mb-3">Select Individual Resource:</p>
                <Button 
                  onClick={() => handlePayment('facebook_ecosystem')}
                  disabled={isProcessing && selectedPackage === 'facebook_ecosystem'}
                  variant="outline"
                  className="w-full"
                >
                  <FacebookIcon className="w-4 h-4 mr-2" />
                  {isProcessing && selectedPackage === 'facebook_ecosystem' ? 'Processing...' : 'Facebook - $27'}
                </Button>
                <Button 
                  onClick={() => handlePayment('instagram_ecosystem')}
                  disabled={isProcessing && selectedPackage === 'instagram_ecosystem'}
                  variant="outline"
                  className="w-full"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  {isProcessing && selectedPackage === 'instagram_ecosystem' ? 'Processing...' : 'Instagram - $27'}
                </Button>
                <Button 
                  onClick={() => handlePayment('tiktok_ecosystem')}
                  disabled={isProcessing && selectedPackage === 'tiktok_ecosystem'}
                  variant="outline"
                  className="w-full"
                >
                  <Video className="w-4 h-4 mr-2" />
                  {isProcessing && selectedPackage === 'tiktok_ecosystem' ? 'Processing...' : 'TikTok - $27'}
                </Button>
              </div>
            </Card>

            {/* Complete - FEATURED */}
            <Card className="p-8 border-4 border-green-600 transform scale-105 shadow-2xl relative">
              <Badge className="absolute -top-3 right-4 bg-red-600 text-white px-4 py-1 font-bold">
                BEST VALUE
              </Badge>
              <h3 className="text-2xl font-bold text-green-600 mb-4">Complete Ecosystem</h3>
              <div className="text-5xl font-black text-red-600 mb-2">$97</div>
              <p className="text-sm text-gray-600 mb-6">Everything to become a bestseller</p>
              <div className="mb-8 space-y-2 text-sm font-medium">
                {[
                  "ALL 4 Resources (400+ platforms)",
                  "Author Resource Guide",
                  "Case Study Blueprint bonus",
                  "30-Day content calendar",
                  "Viral content formulas",
                  "Email & DM templates",
                  "Instant digital download"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => handlePayment('complete_ecosystem')}
                disabled={isProcessing && selectedPackage === 'complete_ecosystem'}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-6 text-lg"
              >
                {isProcessing && selectedPackage === 'complete_ecosystem' ? 'Processing...' : 'GET INSTANT ACCESS →'}
              </Button>
              <p className="text-center text-sm text-red-600 font-bold mt-3">⚡ Most Popular</p>
            </Card>

            {/* VIP */}
            <Card className="p-8 border-2 border-gray-300 opacity-75">
              <Badge className="bg-gray-600 text-white px-4 py-1 text-sm font-bold mb-4">COMING SOON</Badge>
              <h3 className="text-2xl font-bold text-gray-700 mb-4">VIP Package</h3>
              <div className="text-4xl font-black text-gray-600 mb-2">$297</div>
              <p className="text-sm text-gray-600 mb-6">Ecosystem + Strategy Session</p>
              <div className="mb-6 space-y-2 text-sm text-gray-600">
                <div className="flex items-start"><CheckCircle className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" /><span>Everything in Complete</span></div>
                <div className="flex items-start"><CheckCircle className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" /><span>1-hour consultation</span></div>
                <div className="flex items-start"><CheckCircle className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" /><span>Personalized roadmap</span></div>
              </div>
              <Button disabled variant="outline" className="w-full">Coming Soon</Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-white text-gray-900 p-12 text-center">
            <div className="w-40 h-40 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-8 border-8 border-yellow-400">
              <div className="text-xl font-black">30-DAY<br/>MONEY BACK<br/>GUARANTEE</div>
            </div>
            <h2 className="text-4xl font-black text-red-600 mb-6">100% Risk-Free Promise</h2>
            <p className="text-lg mb-4">Use the ecosystem for 30 days. If you don't see increased visibility and sales potential, email us for a 100% refund.</p>
            <p className="text-xl font-black text-green-600 mt-6">ZERO risk and EVERYTHING to gain.</p>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <section className="py-8 px-4 bg-black text-white text-center">
        <p className="text-lg font-semibold mb-2">© 2025 eBlackBookListings Publishing</p>
        <p className="text-sm text-gray-400">Created by Audrey Egypt Young</p>
        <p className="text-sm text-gray-400 mt-2">
          <a href="mailto:eBlackBookListings@gmail.com" className="text-green-400 hover:text-green-300">
            eBlackBookListings@gmail.com
          </a>
        </p>
      </section>
    </div>
  );
};

export default AuthorEcosystemSalesPage;
